import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  AlertTriangle,
  ChevronRight,
  Upload,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';

interface KycStatusData {
  origin?: {
    status: string;
    submittedAt?: string;
    reviewedAt?: string;
    documentsCount: number;
    rejectionReason?: string;
    additionalDocsReason?: string;
    resubmissionCount?: number;
  };
  destination?: {
    status: string;
    submittedAt?: string;
    reviewedAt?: string;
    documentsCount: number;
    rejectionReason?: string;
    additionalDocsReason?: string;
    resubmissionCount?: number;
  };
  canOperate: {
    receive: boolean;
    send: boolean;
  };
  pendingActions: string[];
  estimatedReviewDate?: string;
  resubmissionCount?: number;
  rejectionReason?: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  DRAFT: { label: 'Rascunho', color: 'text-surface-400', icon: Upload },
  SUBMITTED: { label: 'Enviado', color: 'text-blue-400', icon: Clock },
  PENDING_REVIEW: { label: 'Em Análise', color: 'text-yellow-400', icon: Clock },
  IN_REVIEW: { label: 'Em Análise', color: 'text-yellow-400', icon: Clock },
  APPROVED: { label: 'Aprovado', color: 'text-green-400', icon: CheckCircle2 },
  REJECTED: { label: 'Rejeitado', color: 'text-red-400', icon: XCircle },
  ADDITIONAL_DOCS_REQUIRED: { label: 'Docs Adicionais', color: 'text-orange-400', icon: AlertTriangle },
};

export function KycStatusCard() {
  const { user } = useAuthStore();
  const virtualAccountId = user?.virtualAccountId;

  const { data: kycStatus, isLoading } = useQuery<KycStatusData>({
    queryKey: ['kyc-status', virtualAccountId],
    queryFn: async () => {
      if (!virtualAccountId) {
        // Return default status if no virtual account
        return {
          origin: undefined,
          destination: undefined,
          canOperate: { receive: false, send: false },
          pendingActions: ['Você precisa de uma conta virtual para submeter KYC'],
        };
      }
      const response = await api.get(`/kyc/my-status/${virtualAccountId}`);
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    enabled: true, // Always enabled, handles missing virtualAccountId internally
  });

  if (isLoading) {
    return (
      <div className="glass rounded-xl p-4 animate-pulse">
        <div className="h-4 bg-surface-700 rounded w-1/3 mb-3" />
        <div className="h-8 bg-surface-700 rounded w-2/3" />
      </div>
    );
  }

  // Se não há dados de KYC, mostrar card para iniciar
  if (!kycStatus?.origin && !kycStatus?.destination) {
    return (
      <Link to="/banking/kyc" className="block">
        <div className="glass rounded-xl p-4 border border-yellow-500/30 hover:border-yellow-500/50 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-medium text-surface-100">Verificação KYC Pendente</h3>
                <p className="text-sm text-surface-400">
                  Complete sua verificação para operar
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-surface-400" />
          </div>
        </div>
      </Link>
    );
  }

  // Determinar status geral
  const originStatus = kycStatus?.origin?.status || 'DRAFT';
  const destStatus = kycStatus?.destination?.status || 'DRAFT';
  
  const originConfig = statusConfig[originStatus] || statusConfig.DRAFT;
  const destConfig = statusConfig[destStatus] || statusConfig.DRAFT;

  // Verificar se precisa de ação
  const needsAction = 
    originStatus === 'REJECTED' || 
    originStatus === 'ADDITIONAL_DOCS_REQUIRED' ||
    destStatus === 'REJECTED' || 
    destStatus === 'ADDITIONAL_DOCS_REQUIRED' ||
    originStatus === 'DRAFT' ||
    destStatus === 'DRAFT';

  const allApproved = originStatus === 'APPROVED' && destStatus === 'APPROVED';

  return (
    <Link to="/banking/kyc/status" className="block">
      <div className={cn(
        "glass rounded-xl p-4 transition-colors",
        allApproved 
          ? "border border-green-500/30 hover:border-green-500/50"
          : needsAction
          ? "border border-yellow-500/30 hover:border-yellow-500/50"
          : "border border-surface-700 hover:border-surface-600"
      )}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-surface-100">Verificação KYC</h3>
          <ChevronRight className="w-5 h-5 text-surface-400" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* KYC de Origem */}
          <div className="flex items-center gap-2">
            <originConfig.icon className={cn("w-4 h-4", originConfig.color)} />
            <div>
              <p className="text-xs text-surface-500">Origem</p>
              <p className={cn("text-sm font-medium", originConfig.color)}>
                {originConfig.label}
              </p>
            </div>
          </div>

          {/* KYC de Destino */}
          <div className="flex items-center gap-2">
            <destConfig.icon className={cn("w-4 h-4", destConfig.color)} />
            <div>
              <p className="text-xs text-surface-500">Destino</p>
              <p className={cn("text-sm font-medium", destConfig.color)}>
                {destConfig.label}
              </p>
            </div>
          </div>
        </div>

        {/* Ações pendentes */}
        {kycStatus?.pendingActions && kycStatus.pendingActions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-surface-700">
            <p className="text-xs text-yellow-400">
              {kycStatus.pendingActions[0]}
            </p>
          </div>
        )}

        {/* SLA Estimation for IN_REVIEW */}
        {kycStatus?.estimatedReviewDate && !allApproved && (
          <div className="mt-3 pt-3 border-t border-surface-700 flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            <p className="text-xs text-blue-400">
              Previsão: {new Date(kycStatus.estimatedReviewDate).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        )}

        {/* Rejection reason */}
        {kycStatus?.rejectionReason && (originStatus === 'REJECTED' || destStatus === 'REJECTED') && (
          <div className="mt-3 pt-3 border-t border-surface-700">
            <p className="text-xs text-red-400">
              Motivo: {kycStatus.rejectionReason}
            </p>
            {(kycStatus.resubmissionCount === undefined || kycStatus.resubmissionCount < 3) && (
              <p className="text-xs text-primary-400 mt-1">
                Clique para reenviar documentos
              </p>
            )}
            {kycStatus.resubmissionCount !== undefined && kycStatus.resubmissionCount >= 3 && (
              <p className="text-xs text-surface-500 mt-1">
                Limite de reenvios atingido. Contate o suporte.
              </p>
            )}
          </div>
        )}

        {/* Status de operação */}
        {allApproved && (
          <div className="mt-3 pt-3 border-t border-surface-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <p className="text-xs text-green-400">
              Verificação completa - Todas as operações liberadas
            </p>
          </div>
        )}
      </div>
    </Link>
  );
}
